/**
 * @fileoverview User-facing copy for every error the app can surface.
 * @module lib/errors/errorMessages
 *
 * @description
 * Single registry, so the wording stays consistent between the route error
 * boundary, the render error boundary and inline error states.
 */

import { ErrorCode } from "./AppError"

/** What the UI needs to render an error state. */
export interface ErrorPresentation {
	/** Short headline. */
	readonly title: string
	/** Reassuring explanation, in plain language. */
	readonly message: string
}

/** Fallback used whenever nothing more specific matches. */
export const DEFAULT_ERROR_PRESENTATION: ErrorPresentation = {
	title: "Erreur inattendue",
	message:
		"Une erreur inattendue s'est produite. Veuillez réessayer plus tard.",
}

/** HTTP status -> copy. */
const BY_STATUS: Readonly<Record<number, ErrorPresentation>> = {
	400: {
		title: "Requête incorrecte",
		message: "La requête envoyée n'est pas valide.",
	},
	401: {
		title: "Non autorisé",
		message: "Vous devez être connecté pour accéder à cette ressource.",
	},
	403: {
		title: "Accès refusé",
		message:
			"Vous n'avez pas les permissions nécessaires pour accéder à cette page.",
	},
	404: {
		title: "Page non trouvée",
		message: "La page que vous cherchez n’existe pas.",
	},
	500: {
		title: "Erreur serveur",
		message: "Une erreur est survenue. Réessayez dans un instant.",
	},
	503: {
		title: "Service indisponible",
		message: "Le service est temporairement indisponible. Réessayez.",
	},
}

/** Error code -> copy, for failures that never got an HTTP status. */
const BY_CODE: Readonly<Record<ErrorCode, ErrorPresentation>> = {
	[ErrorCode.NETWORK]: {
		title: "Connexion impossible",
		message:
			"Impossible de joindre le serveur. Vérifiez votre connexion internet.",
	},
	[ErrorCode.TIMEOUT]: {
		title: "Délai dépassé",
		message: "Le serveur met trop de temps à répondre. Réessayez.",
	},
	[ErrorCode.VALIDATION]: {
		title: "Données invalides",
		message: "Les données reçues ne correspondent pas au format attendu.",
	},
	[ErrorCode.STORAGE]: {
		title: "Données inaccessibles",
		message:
			"Impossible de lire ou d'écrire vos données sur cet appareil. " +
			"Vérifiez que le stockage du navigateur n'est pas désactivé.",
	},
	[ErrorCode.UNAUTHORIZED]: BY_STATUS[401] ?? DEFAULT_ERROR_PRESENTATION,
	[ErrorCode.FORBIDDEN]: BY_STATUS[403] ?? DEFAULT_ERROR_PRESENTATION,
	[ErrorCode.NOT_FOUND]: BY_STATUS[404] ?? DEFAULT_ERROR_PRESENTATION,
	[ErrorCode.HTTP]: BY_STATUS[500] ?? DEFAULT_ERROR_PRESENTATION,
	[ErrorCode.UNKNOWN]: DEFAULT_ERROR_PRESENTATION,
}

/**
 * Resolves the copy to display, most specific source first.
 *
 * @param input - Known status and/or code.
 * @returns The presentation to render. Never throws, never returns undefined.
 */
export function getErrorPresentation(input: {
	readonly status?: number | undefined
	readonly code?: ErrorCode | undefined
}): ErrorPresentation {
	if (input.status !== undefined) {
		const byStatus = BY_STATUS[input.status]
		if (byStatus !== undefined) return byStatus
	}

	if (input.code !== undefined) {
		return BY_CODE[input.code]
	}

	return DEFAULT_ERROR_PRESENTATION
}
